/**
 * HabitaScoreBreakdown — visualización 4 dimensiones del Habita Score.
 *
 * Cada dimensión (Presupuesto · Ubicación · Espacios · Preferencias) se
 * muestra con barra + porcentaje. El peso relativo (según work_mode y
 * priorities del usuario) se indica sutilmente con el grosor visual.
 */
import React from 'react';
import { DollarSign, MapPin, Home as HomeIcon, Sparkles } from 'lucide-react';

const DIM_META = {
  budget: { label: 'Presupuesto', Icon: DollarSign },
  location: { label: 'Ubicación', Icon: MapPin },
  space: { label: 'Espacios', Icon: HomeIcon },
  preferences: { label: 'Preferencias', Icon: Sparkles },
};

const COLOR_OF = (v) => {
  if (v >= 90) return '#0e7c62';
  if (v >= 75) return '#10b981';
  if (v >= 60) return '#f59e0b';
  if (v >= 40) return '#f97316';
  return '#ef4444';
};

export default function HabitaScoreBreakdown({ score, className = '', testId = 'habita-score-breakdown' }) {
  if (!score) return null;
  const dims = [
    ['budget', score.budget_score],
    ['location', score.location_score ?? score.mobility_score],
    ['space', score.space_score],
    ['preferences', score.preferences_score],
  ];
  const weights = score.weights || {};

  return (
    <div className={`space-y-2 ${className}`} data-testid={testId}>
      {dims.map(([key, value]) => {
        const meta = DIM_META[key];
        const v = typeof value === 'number' ? value : 0;
        const w = weights[key];
        const color = COLOR_OF(v);
        return (
          <div
            key={key}
            className="flex items-center gap-3"
            data-testid={`breakdown-${key}`}
          >
            <meta.Icon className="w-4 h-4 text-habita shrink-0" style={{ color }} />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between mb-0.5">
                <span className="text-xs font-semibold text-habita-dark">{meta.label}</span>
                <span className="text-sm font-serif font-bold" style={{ color }} data-testid={`breakdown-${key}-value`}>
                  {v}%
                </span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${Math.max(4, v)}%`, background: color }}
                />
              </div>
              {typeof w === 'number' && (
                <p className="text-[10px] text-gray-400 mt-0.5 uppercase tracking-widest">
                  Peso {Math.round(w * 100)}%
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
